import { useState } from 'react';
import { Badge, Button, Card, Heading, Stack, Text } from '../../ui';
import { formatElapsed } from '../../racing/format';
import type { Bracket, Match, MatchEntry } from '../../racing/types';

interface BracketViewProps {
  /** The bracket to render. */
  readonly bracket: Bracket;
  /** Whether the caller may record results. */
  readonly canRecord: boolean;
  /** Records a time, in milliseconds, for a contestant in a match. */
  readonly onRecord: (matchId: string, contestantId: string, elapsedMs: number) => void;
  /** Whether a record request is in flight. */
  readonly recording: boolean;
}

/**
 * Renders a generated bracket as rounds of matches, with inline result entry while the caller may score.
 *
 * @param props - The bracket, the record permission and callback.
 * @returns The bracket element.
 */
/** The order sections are displayed in, and the only sections a double-elimination bracket uses. */
const SECTION_ORDER = ['Winners', 'Losers', 'GrandFinal'] as const;

export function BracketView(props: BracketViewProps): React.JSX.Element {
  const { bracket, canRecord, onRecord, recording } = props;
  const sections = SECTION_ORDER.filter((section) => bracket.matches.some((match) => match.section === section));
  const multiSection = sections.length > 1;

  const groups = sections.flatMap((section) => {
    const sectionMatches = bracket.matches.filter((match) => match.section === section);
    const rounds = [...new Set(sectionMatches.map((match) => match.round))].sort((a, b) => a - b);
    const maxRound = rounds.length > 0 ? rounds[rounds.length - 1]! : 0;
    return rounds.map((round) => ({
      key: `${section}-${round}`,
      label: groupLabel(section, round, maxRound, multiSection),
      matches: sectionMatches
        .filter((match) => match.round === round)
        .sort((a, b) => a.positionInRound - b.positionInRound),
    }));
  });

  return (
    <Stack gap="md">
      {groups.map((group) => (
        <section key={group.key} aria-label={group.label}>
          <Heading level={3}>{group.label}</Heading>
          <div className="gm-bracket-round">
            {group.matches.map((match) => (
              <MatchCard key={match.id} match={match} canRecord={canRecord} onRecord={onRecord} recording={recording} />
            ))}
          </div>
        </section>
      ))}
    </Stack>
  );
}

/**
 * Labels a round group. A single-section bracket (single elimination, heats) keeps the plain
 * "Round N" / "Final"; a double-elimination bracket names the winners, losers and grand-final
 * sections so the two brackets do not read as one.
 */
function groupLabel(section: string, round: number, maxRound: number, multiSection: boolean): string {
  if (!multiSection) {
    return round === maxRound ? 'Final' : `Round ${round}`;
  }

  switch (section) {
    case 'Losers':
      return round === maxRound ? 'Losers Final' : `Losers Round ${round}`;
    case 'GrandFinal':
      return round === 1 ? 'Grand Final' : 'Grand Final (reset)';
    default:
      return round === maxRound ? 'Winners Final' : `Winners Round ${round}`;
  }
}

function MatchCard(props: {
  readonly match: Match;
  readonly canRecord: boolean;
  readonly onRecord: (matchId: string, contestantId: string, elapsedMs: number) => void;
  readonly recording: boolean;
}): React.JSX.Element {
  const { match, canRecord, onRecord, recording } = props;
  const decided = match.status === 'Completed' || match.status === 'Bye';

  return (
    <Card ariaLabel={`Match ${match.round}-${match.positionInRound + 1}`}>
      <Stack gap="sm">
        <Stack direction="row" justify="space-between" align="center" wrap>
          <Text muted size="sm">
            {match.status === 'Bye' ? 'Bye' : `Heat ${match.positionInRound + 1}`}
          </Text>
          {match.hasConflict && <Badge tone="danger">Needs review</Badge>}
        </Stack>
        {match.entries
          .slice()
          .sort((a, b) => a.position - b.position)
          .map((entry) => (
            <EntryRow
              key={entry.id}
              entry={entry}
              official={officialTimeFor(match, entry.contestantId)}
              isWinner={match.winnerContestantId != null && entry.contestantId === match.winnerContestantId}
              canRecord={canRecord && !decided && entry.contestantId != null}
              onRecord={(elapsedMs) => {
                if (entry.contestantId != null) {
                  onRecord(match.id, entry.contestantId, elapsedMs);
                }
              }}
              recording={recording}
            />
          ))}
      </Stack>
    </Card>
  );
}

function officialTimeFor(match: Match, contestantId: string | null): number | null {
  if (contestantId == null) {
    return null;
  }

  const official = match.results.find((result) => result.contestantId === contestantId && result.isOfficial);
  return official?.elapsedTimeMs ?? null;
}

function EntryRow(props: {
  readonly entry: MatchEntry;
  readonly official: number | null;
  readonly isWinner: boolean;
  readonly canRecord: boolean;
  readonly onRecord: (elapsedMs: number) => void;
  readonly recording: boolean;
}): React.JSX.Element {
  const { entry, official, isWinner, canRecord, onRecord, recording } = props;
  const [seconds, setSeconds] = useState('');

  const record = (): void => {
    const parsed = Number.parseFloat(seconds);
    if (!Number.isNaN(parsed)) {
      onRecord(Math.round(parsed * 1000));
      setSeconds('');
    }
  };

  return (
    <Stack direction="row" gap="sm" justify="space-between" align="center" wrap>
      <Stack direction="row" gap="sm" align="center" wrap>
        <Text weight={isWinner ? 'bold' : 'regular'}>{entry.contestantName ?? 'Awaiting winner'}</Text>
        {isWinner && <Badge tone="success">Winner</Badge>}
      </Stack>
      <Stack direction="row" gap="sm" align="center" wrap>
        <Text muted size="sm">
          {formatElapsed(official)}
        </Text>
        {canRecord && (
          <>
            <input
              className="gm-input gm-input--inline"
              type="number"
              inputMode="decimal"
              step="0.001"
              placeholder="seconds"
              aria-label={`Time for ${entry.contestantName ?? 'entry'}`}
              value={seconds}
              onChange={(event) => setSeconds(event.target.value)}
            />
            <Button disabled={seconds.trim() === '' || recording} onClick={record}>
              Record
            </Button>
          </>
        )}
      </Stack>
    </Stack>
  );
}
